import type {
  HealthLowFundingUpstreamEntry,
  HealthPricedModelEntry,
  HealthUnhealthyUpstreamEntry,
  OpsDashboardAlertsResponse,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useDashboardAlerts } from '../api/useDashboardAlerts.ts';
import { formatUsdCents } from './formatters.ts';
import { useT } from '../i18n/index.ts';

/**
 * Dashboard proactive-alert cards (Epic #109 / P0.3).
 *
 * Renders four cards stacked in a responsive grid:
 *
 *   - Unrouted SKUs      (priced × no enabled upstream)
 *   - Single-point SKUs  (priced × exactly one enabled upstream)
 *   - Low-funding        (funding batches with < 20% remaining capacity)
 *   - Unhealthy 24h      (upstreams with ≥ 20 req + > 20% 5xx failure)
 *
 * The cards are rendered only when their respective arrays are
 * non-empty. When every array is empty we show an "all clear"
 * notice. Loading and error surfaces mirror the router-stats panel
 * below so operators see a consistent failure mode across the page.
 *
 * Formatting / empty-name handling:
 *   - USD amounts render in fractional cents with 2 decimal places.
 *   - Upstream names fall back to the id when the gateway's name map
 *     didn't include the row (upstream soft-deleted between snapshot +
 *     merge). This matches the convention used for
 *     `unhealthy_upstreams_24h` rows in the table.
 */

const MAX_ROWS_PER_CARD = 5;

export function DashboardAlerts(): JSX.Element {
  const t = useT();
  const query = useDashboardAlerts();

  if (query.isPending) {
    return (
      <section
        data-testid="dashboard-alerts-loading"
        className="rounded-xl bg-surface-container p-6"
      >
        <header className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-on-surface">
            {t('dashboard.alerts.title')}
          </h3>
          <p className="text-sm text-outline">{t('dashboard.alerts.subtitle')}</p>
        </header>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-lg bg-surface-container-low"
              aria-hidden="true"
            />
          ))}
        </div>
        <span className="sr-only">{t('dashboard.alerts.loading')}</span>
      </section>
    );
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        data-testid="dashboard-alerts-error"
        className="flex items-center justify-between gap-4 rounded-xl bg-error-container px-6 py-4 text-on-error-container"
      >
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold">{t('dashboard.alerts.error.title')}</span>
          <span className="text-xs">
            {query.error instanceof OpsApiError
              ? `${query.error.code}: ${query.error.message}`
              : t('common.network_error')}
          </span>
        </div>
        <button
          type="button"
          onClick={() => void query.refetch()}
          data-testid="dashboard-alerts-retry"
          className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-error-container"
        >
          {t('common.retry')}
        </button>
      </section>
    );
  }

  const alerts: OpsDashboardAlertsResponse = query.data;
  const totalCount =
    alerts.unrouted_skus.length +
    alerts.single_point_skus.length +
    alerts.low_funding_upstreams.length +
    alerts.unhealthy_upstreams_24h.length;

  if (totalCount === 0) {
    return (
      <section
        data-testid="dashboard-alerts-all-clear"
        className="rounded-xl bg-surface-container p-6"
      >
        <header className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-on-surface">
            {t('dashboard.alerts.title')}
          </h3>
          <p className="text-sm text-outline">{t('dashboard.alerts.subtitle')}</p>
        </header>
        <p className="mt-4 text-sm text-on-surface-variant">{t('dashboard.alerts.all_clear')}</p>
      </section>
    );
  }

  return (
    <section data-testid="dashboard-alerts" className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-on-surface">{t('dashboard.alerts.title')}</h3>
        <p className="text-sm text-outline">{t('dashboard.alerts.subtitle')}</p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {alerts.unrouted_skus.length > 0 ? (
          <AlertCard
            testId="dashboard-alert-unrouted"
            title={t('dashboard.alerts.unrouted.title')}
            description={t('dashboard.alerts.unrouted.description')}
            count={t('dashboard.alerts.unrouted.count', { count: alerts.unrouted_skus.length })}
            rows={alerts.unrouted_skus.slice(0, MAX_ROWS_PER_CARD).map(formatSkuRow)}
            remainder={Math.max(0, alerts.unrouted_skus.length - MAX_ROWS_PER_CARD)}
            tone="critical"
          />
        ) : null}

        {alerts.single_point_skus.length > 0 ? (
          <AlertCard
            testId="dashboard-alert-single-point"
            title={t('dashboard.alerts.single_point.title')}
            description={t('dashboard.alerts.single_point.description')}
            count={t('dashboard.alerts.single_point.count', {
              count: alerts.single_point_skus.length,
            })}
            rows={alerts.single_point_skus.slice(0, MAX_ROWS_PER_CARD).map(formatSkuRow)}
            remainder={Math.max(0, alerts.single_point_skus.length - MAX_ROWS_PER_CARD)}
            tone="warning"
          />
        ) : null}

        {alerts.low_funding_upstreams.length > 0 ? (
          <AlertCard
            testId="dashboard-alert-low-funding"
            title={t('dashboard.alerts.low_funding.title')}
            description={t('dashboard.alerts.low_funding.description')}
            count={t('dashboard.alerts.low_funding.count', {
              count: alerts.low_funding_upstreams.length,
            })}
            rows={alerts.low_funding_upstreams
              .slice(0, MAX_ROWS_PER_CARD)
              .map((r) => formatLowFundingRow(r, t))}
            remainder={Math.max(0, alerts.low_funding_upstreams.length - MAX_ROWS_PER_CARD)}
            tone="warning"
          />
        ) : null}

        {alerts.unhealthy_upstreams_24h.length > 0 ? (
          <AlertCard
            testId="dashboard-alert-unhealthy"
            title={t('dashboard.alerts.unhealthy.title')}
            description={t('dashboard.alerts.unhealthy.description')}
            count={t('dashboard.alerts.unhealthy.count', {
              count: alerts.unhealthy_upstreams_24h.length,
            })}
            rows={alerts.unhealthy_upstreams_24h
              .slice(0, MAX_ROWS_PER_CARD)
              .map((r) => formatUnhealthyRow(r, t))}
            remainder={Math.max(
              0,
              alerts.unhealthy_upstreams_24h.length - MAX_ROWS_PER_CARD,
            )}
            tone="critical"
          />
        ) : null}
      </div>
    </section>
  );
}

type AlertTone = 'critical' | 'warning';

interface AlertCardProps {
  readonly testId: string;
  readonly title: string;
  readonly description: string;
  readonly count: string;
  readonly rows: readonly string[];
  readonly remainder: number;
  readonly tone: AlertTone;
}

function AlertCard({
  testId,
  title,
  description,
  count,
  rows,
  remainder,
  tone,
}: AlertCardProps): JSX.Element {
  // Tone drives the accent color on the count chip and the thin border
  // strip. We stay inside DESIGN.md tokens (no raw hex) and rely on the
  // on-container text colors to pair with the surface token.
  const toneClasses =
    tone === 'critical'
      ? 'border-error-container text-on-error-container bg-error-container'
      : 'border-tertiary-container text-on-tertiary-container bg-tertiary-container';
  return (
    <article
      data-testid={testId}
      className="flex flex-col gap-3 rounded-xl bg-surface-container p-5"
    >
      <header className="flex items-start justify-between gap-3">
        <h4 className="text-sm font-semibold tracking-tight text-on-surface">{title}</h4>
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] ${toneClasses}`}
        >
          {count}
        </span>
      </header>
      <p className="text-xs text-outline">{description}</p>
      <ul className="flex flex-col gap-1.5 text-[12px] text-on-surface">
        {rows.map((row, idx) => (
          <li key={idx} className="font-mono leading-tight text-on-surface-variant">
            {row}
          </li>
        ))}
        {remainder > 0 ? (
          <li className="text-[11px] italic text-outline">+{remainder}</li>
        ) : null}
      </ul>
    </article>
  );
}

function formatSkuRow(entry: HealthPricedModelEntry): string {
  const displayName = entry.display_name;
  if (displayName !== null && displayName.length > 0) {
    return `${displayName} · ${entry.vendor} / ${entry.channel} / ${entry.model} / ${entry.protocol}`;
  }
  return `${entry.vendor} / ${entry.channel} / ${entry.model} / ${entry.protocol}`;
}

function formatLowFundingRow(
  entry: HealthLowFundingUpstreamEntry & { upstream_name: string },
  t: ReturnType<typeof useT>,
): string {
  const pct = formatPct(entry.pct_remaining);
  const remaining = formatUsdCents(entry.remaining_usd_cents);
  const capacity = formatUsdCents(entry.capacity_usd_cents);
  if (entry.upstream_name.length > 0) {
    return t('dashboard.alerts.low_funding.row', {
      name: entry.upstream_name,
      pct,
      remaining,
      capacity,
    });
  }
  return t('dashboard.alerts.low_funding.row_unknown', {
    id: entry.upstream_id,
    pct,
    remaining,
    capacity,
  });
}

function formatUnhealthyRow(
  entry: HealthUnhealthyUpstreamEntry,
  t: ReturnType<typeof useT>,
): string {
  const rate = formatPct(entry.failure_rate);
  if (entry.upstream_name.length > 0) {
    return t('dashboard.alerts.unhealthy.row', {
      name: entry.upstream_name,
      rate,
      failures: entry.failures,
      requests: entry.requests,
    });
  }
  return t('dashboard.alerts.unhealthy.row_unknown', {
    id: entry.upstream_id,
    rate,
    failures: entry.failures,
    requests: entry.requests,
  });
}

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(2)}%`;
}
